// IndexedDB bootstrap (idb wrapper). One DB, two stores:
//   booths  — BoothRecord (+ denormalised show_booth_key), keyPath `id`.
//   photos  — StoredPhoto (compressed bytes), keyPath `local_id`, indexed by booth.
// Photo bytes are stored as ArrayBuffers (see StoredPhoto): iOS/WebKit IndexedDB
// is unreliable at persisting Blob values, so we keep buffers and rebuild Blobs
// on read. Capture stays fully offline (no object-URL persistence needed).

import { openDB } from 'idb';
import type { DBSchema, IDBPDatabase } from 'idb';
import type { BoothRecord, StoredPhoto, HitListTarget } from './schema';
import { showBoothKey } from './schema';
import type { StoredSnapshot } from '../snapshot/types';

const DB_NAME = 'booth-tracker';
// v2: added the `snapshot` store (Slice 3 offline data snapshot).
// v3 (v0.5): added BoothRecord.deleted tombstone + 4 free-text fields. These are
// plain optional properties on the record value — no new store/index/keyPath — so
// the bump is purely additive: existing rows simply read the new fields as
// undefined (treated as "not deleted" / empty). The existence-guarded upgrade
// below therefore has nothing to create for the 2→3 step.
// v4 (v0.7): added the `hitlist` store (shared exhibitor hit list). Additive — a
// NEW store only; booths/photos/snapshot are untouched, so existing captures and
// the cached snapshot survive the upgrade exactly as they did for 2→3.
const DB_VERSION = 4;

// IndexedDB indexes a stored property, not a computed value, so we persist a
// denormalised `show_booth_key` alongside the domain record for the merge index.
export type StoredBooth = BoothRecord & { show_booth_key: string };

interface BoothTrackerDB extends DBSchema {
  booths: {
    key: string;
    value: StoredBooth;
    indexes: {
      show_booth: string;
      sync_status: string;
      updated_at: string;
    };
  };
  photos: {
    key: string;
    value: StoredPhoto;
    indexes: {
      booth_id: string;
    };
  };
  snapshot: {
    key: string;
    value: StoredSnapshot;
  };
  hitlist: {
    key: string; // exhibitor_key — the cross-device merge identity
    value: HitListTarget;
    indexes: {
      sync_status: string;
      show_key: string;
    };
  };
}

let dbPromise: Promise<IDBPDatabase<BoothTrackerDB>> | null = null;

export function getDB(): Promise<IDBPDatabase<BoothTrackerDB>> {
  if (!dbPromise) {
    dbPromise = openDB<BoothTrackerDB>(DB_NAME, DB_VERSION, {
      // Guard each store by existence so this runs correctly both for a fresh DB
      // (oldVersion 0) and a v1→v2 upgrade (booths/photos already present).
      upgrade(db) {
        if (!db.objectStoreNames.contains('booths')) {
          const booths = db.createObjectStore('booths', { keyPath: 'id' });
          // Non-unique: same show+booth may exist transiently before a Slice-2 merge.
          booths.createIndex('show_booth', 'show_booth_key', { unique: false });
          booths.createIndex('sync_status', 'sync_status', { unique: false });
          booths.createIndex('updated_at', 'updated_at', { unique: false });
        }
        if (!db.objectStoreNames.contains('photos')) {
          const photos = db.createObjectStore('photos', { keyPath: 'local_id' });
          photos.createIndex('booth_id', 'booth_id', { unique: false });
        }
        if (!db.objectStoreNames.contains('snapshot')) {
          // Single-row store (id: 'current') holding the latest offline snapshot.
          db.createObjectStore('snapshot', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('hitlist')) {
          // Keyed by exhibitor_key, NOT the uuid: that's the identity the backend
          // merges on, so one exhibitor can only ever have one row per device.
          const hitlist = db.createObjectStore('hitlist', { keyPath: 'exhibitor_key' });
          hitlist.createIndex('sync_status', 'sync_status', { unique: false });
          hitlist.createIndex('show_key', 'show_key', { unique: false });
        }
      },
    });
  }
  return dbPromise;
}

export function withShowBoothKey(record: BoothRecord): StoredBooth {
  return { ...record, show_booth_key: showBoothKey(record.show, record.booth_number) };
}

export type { BoothTrackerDB };
